import type { FC, PropsWithChildren } from 'react';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Heading } from '@cognite/cogs-core-v11/tokens/typography/typography.tsx';
import { Loader } from '@cognite/cogs-core-v11/components/loader/loader.tsx';

import { USER_INSTANCE_SPACE } from '../../constants/user-data-model';
import { UserErrorComponent } from '../user-onboarding-provider/components/user-error-component';

import { CdmCurrentUserProviderContext } from './cdm-current-user-provider.context';
import type { CurrentUserContextValue } from './current-user-context';
import { CurrentUserContext } from './current-user-context';

export const CdmCurrentUserProvider: FC<PropsWithChildren> = ({ children }) => {
  const { useInfieldLogout, useCurrentUserQuery } = useContext(
    CdmCurrentUserProviderContext
  );

  const { t } = useTranslation('user');
  const { logout } = useInfieldLogout();

  const {
    data: currentUser,
    isError: isUserDataError,
    error,
  } = useCurrentUserQuery();

  const value = useMemo<CurrentUserContextValue | null>(() => {
    if (!currentUser) {
      return null;
    }
    return {
      user: currentUser,
    };
  }, [currentUser]);

  if (isUserDataError && !currentUser) {
    return (
      <UserErrorComponent
        userSpace={USER_INSTANCE_SPACE}
        error={error}
        onLogout={logout}
      />
    );
  }

  if (!currentUser) {
    return (
      <Loader
        infoText={
          <Heading level={6}>
            {t('loadingUserData', 'Loading user data')}
          </Heading>
        }
      />
    );
  }

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
};
